// Visual representation of a task during drag operations.
import { TaskUIProvider } from '@/features/tasks/context/TaskUIContext';
import ScheduledTask from '@/features/planning/components/time-grid/ScheduledTask';
import TaskListItem from '@/features/tasks/components/items/TaskItemBase';
import GridTaskItem from '@/features/planning/components/shared/GridTaskItem';
import { resolveDragOverlayHeight } from '@/features/planning/components/shared/dragOverlayHeight';

const defaultTaskUIValue = {
  view: 'default',
  indentationType: 'padding',
  onStartAddingSubtask: () => {},
  onRebalance: (_event) => {},
  duplicateOrderKeyTaskIds: new Set(),
  selectedTaskIds: [],
  isMultiSelectActive: false,
};
const todayTaskUIValue = {
  view: 'today',
  indentationType: 'none',
  onStartAddingSubtask: () => {},
  onRebalance: (_event) => {},
  duplicateOrderKeyTaskIds: new Set(),
  selectedTaskIds: [],
  isMultiSelectActive: false,
};

/**
 * @typedef {import('@/features/tasks/hooks/list/use-list-dnd').ActiveItem} ActiveItem
 */

/**
 * Renders the content for the drag overlay based on the active item type.
 * @param {Object} props
 * @param {ActiveItem|null} props.activeItem
 * @param {'day'|'week'|'month'} [props.gridView] - Which plan-tab is active.
 *   The drop target is always that tab's grid, so the overlay adopts the
 *   grid's task shape immediately on pickup instead of only once hovering
 *   a timeslot/day cell.
 * @param {number|null} [props.overGridWidth] - Measured width (px) of the
 *   hovered day-grid droppable. Column width is flex-based, not derivable
 *   from duration like height, so it's read live from the drag-over rect.
 */
export const DragOverlayContent = ({ activeItem, gridView, overGridWidth }) => {
  if (!activeItem) return null;
  if (gridView === 'week' || gridView === 'month') {
    return <GridTaskItem task={activeItem.task} dense={gridView === 'month'} />;
  }
  if (gridView === 'day' || activeItem.type === 'scheduled-task') {
    const height = resolveDragOverlayHeight({
      gridView,
      durationMins: activeItem.task?.duration,
      sourceHeight: activeItem.height,
    });
    const width = gridView === 'day' && overGridWidth ? overGridWidth : null;
    return (
      <ScheduledTask
        task={activeItem.task}
        isOverlay
        style={{ height: `${height}px`, ...(width ? { width: `${width}px` } : {}) }}
      />
    );
  }
  if (activeItem.type === 'unscheduled-task') {
    return (
      <TaskUIProvider value={defaultTaskUIValue}>
        <TaskListItem task={activeItem.task} isOverlay />
      </TaskUIProvider>
    );
  }
  if (
    activeItem.type === 'overdue-task' ||
    activeItem.type === 'daily-task' ||
    activeItem.type === 'next-action'
  ) {
    return (
      <TaskUIProvider value={todayTaskUIValue}>
        <TaskListItem
          task={activeItem.task}
          isOverlay
          view="today"
          indentationType="none"
        />
      </TaskUIProvider>
    );
  }
  return null;
};
